import React, { useEffect, useState, useCallback, useMemo } from 'react';

import Heading from '@react/react-spectrum/Heading';
import { TabView, Tab } from '@react/react-spectrum/TabView';
import CircularProgress from '@material-ui/core/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Select from "@react/react-spectrum/Select";
import Autocomplete from "@react/react-spectrum/Autocomplete";
import Textfield from "@react/react-spectrum/Textfield";

import './ViewEditAvailableFlexDiscountsPage.css';
import { getAvailableFlexDiscounts } from '../../../providers/FlexDiscountsAPI';
import { ErrorResponse } from '../../../models/ErrorResponse';
import ErrorDialog from '../../../utils/Dialogs/ErrorDialog';
import { FlexDiscount } from '../../../models/FlexDiscount';
import EditReusableFlexDiscounts from './EditReusableFlexDiscounts';
import ClosedDiscountsTab from './ClosedDiscountsTab';
import FlexDiscountTemplatesTab from './FlexDiscountTemplatesTab';
import {
    isApplicableOnRenewalFlagEnabled,
    isApplicableOnRenewalFlagDevEnabled,
    isFlexDiscountsIntroFlagDevEnabled,
    isFlexDiscountsIntroFlagEnabled,
    isFlexPromoReusabilityFlagDevEnabled,
    isFlexPromoReusabilityFlagEnabled,
    isOdDiscountsFlagDevEnabled,
    isOdDiscountsFlagEnabled
} from '../../../config/FeatureFlags';
import {getCurrentUserDistributorInfo} from "../../../utils/Functions/utils";

function ViewEditAvailableFlexDiscountsPage() {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [allFlexDiscounts, setAllFlexDiscounts] = useState<FlexDiscount[]>([]);
    const [errorResponse, setErrorResponse] = useState<ErrorResponse | undefined>();
    const [selectCountry, setSelectCountry] = useState('');
    const [isApplicableOnRenewal, setIsApplicableOnRenewal] = useState(false);
    const [selectMarketSegment, setSelectMarketSegment] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [countrySearchText, setCountrySearchText] = useState('');

    /**
     * Expand API response so that UI still receives
     * one country per discount object.
     */
    const normalizeDiscounts = (discounts: FlexDiscount[]): FlexDiscount[] => {
        const normalized: FlexDiscount[] = [];

        discounts.forEach((discount) => {
            if(discount.reusableUntil) discount.discountLockEndDate = discount.reusableUntil;

            const countries = discount.applicableCountries || [];

            if (
                !discount.outcomes ||
                discount.outcomes.length === 0 ||
                !discount.outcomes[0].discountValues ||
                discount.outcomes[0].discountValues.length === 0
            ) {
                return;
            }

            countries.forEach((country: string, index: number) => {

                let discountValue;

                if (discount.outcomes[0].type !== "PERCENTAGE_DISCOUNT") {
                    discountValue = discount.outcomes[0].discountValues[index];
                } else {
                    discountValue = discount.outcomes[0].discountValues[0];
                }

                if (discountValue === undefined) return;

                const newOutcome = {
                    ...discount.outcomes[0],
                    discountValues: [discountValue]
                };

                const newDiscount = {
                    ...discount,
                    applicableCountry: country,
                    outcomes: [newOutcome]
                };

                normalized.push(newDiscount);
            });

        });

        return normalized;
    };

    const countryOptions = useMemo(() => {
        const countries = allFlexDiscounts.map(d => d.applicableCountry).filter(Boolean);
        return Array.from(new Set(countries));
    }, [allFlexDiscounts]);

    const getCountryCompletions = useCallback((value: string) => {
        const query = value.trim().toLowerCase();
        if (!query) return countryOptions;
        return countryOptions.filter(country => country.toLowerCase().includes(query));
    }, [countryOptions]);

    const marketSegmentOptions = useMemo(() => {
        const segments = allFlexDiscounts.map(d => d.applicableMarketSegment).filter(Boolean);
        const unique = Array.from(new Set(segments));
        return unique.map(segment => ({ label: segment, value: segment }));
    }, [allFlexDiscounts]);

    const applicableOnRenewalOptions = [
        { label: 'Yes', value: "true" },
        { label: 'No', value: "false" },
    ];

    const isReusabilityEnabled = isFlexPromoReusabilityFlagDevEnabled() || isFlexPromoReusabilityFlagEnabled();
    const isOdDiscountsEnabled = isOdDiscountsFlagDevEnabled() || isOdDiscountsFlagEnabled();

    const fetchFlexDiscounts = useCallback(async () => {
        setIsLoading(true);
        try {
            const userInfo = getCurrentUserDistributorInfo();
            const partnerId = userInfo?.selectedDistributor?.imsOrgId;

            const response = await getAvailableFlexDiscounts(partnerId);
            if (response.httpstatus === 200) {
                let discounts: FlexDiscount[] = response.data;
                if (!isApplicableOnRenewalFlagEnabled() && !isApplicableOnRenewalFlagDevEnabled()) {
                    discounts = discounts.filter((fd) => !fd.applicableOnRenewal);
                }
                if (!isFlexDiscountsIntroFlagDevEnabled() && !isFlexDiscountsIntroFlagEnabled()) {
                    discounts = discounts.filter((fd) => fd.category !== "INTRO");
                    for (const discount of discounts) {
                        delete discount.category;
                    }
                }
                if (!isReusabilityEnabled) {
                    discounts = discounts.filter((fd) => fd.reusableUntil == null);
                }
                const normalized = normalizeDiscounts(discounts);

                setAllFlexDiscounts(normalized);
                setIsError(false);
            } else {
                throw response;
            }
        } catch (e) {
            const err = e as any;
            setErrorResponse({
                httpstatus: err?.httpstatus,
                reason: err?.reason,
                message: err?.message || 'Failed to fetch flex discounts'
            });
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [isReusabilityEnabled]);

    useEffect(() => {
        if (selectedTab === 0) {
            fetchFlexDiscounts();
        }
    }, [fetchFlexDiscounts, selectedTab]);

    // "Closed Discount Templates" and "Closed Discounts" always render after
    // "Edit Reusable Flex Discounts" when both are enabled, but shift to indexes 1/2 when
    // reusability is disabled — since Tab elements are conditionally rendered, their
    // position (and thus their TabView index) is not fixed.
    const flexDiscountTemplatesTabIndex = isReusabilityEnabled ? 2 : 1;
    const closedDiscountsTabIndex = flexDiscountTemplatesTabIndex + 1;
    const currentPartnerId = getCurrentUserDistributorInfo()?.selectedDistributor?.imsOrgId;
    const tabCount = 1 + (isReusabilityEnabled ? 1 : 0) + (isOdDiscountsEnabled ? 2 : 0);

    useEffect(() => {
        if (selectedTab > tabCount - 1) {
            setSelectedTab(0);
        }
    }, [selectedTab, tabCount]);

    const handleDismissErrorDialog = () => {
        sessionStorage.clear();
        window.location.reload();
    };

    const handleFilterChange = useCallback((country: string, marketSegment: string, applicableOnRenewal: boolean) => {
        let filtered = allFlexDiscounts;

        if (country) {
            // When a specific country is selected, show discounts for that country
            // AND discounts with "All Countries" as applicableCountry
            filtered = filtered.filter(discount =>
                discount.applicableCountry === country ||
                discount.applicableCountry === "All Countries"
            );
        }
        // If country is 'ALL_COUNTRIES' or empty, show all discounts (no country filter)

        if (marketSegment) {
            filtered = filtered.filter(discount => discount.applicableMarketSegment === marketSegment);
        }

        if (isApplicableOnRenewalFlagEnabled() || isApplicableOnRenewalFlagDevEnabled()) {
            filtered = filtered.filter(
                discount => discount.applicableOnRenewal === applicableOnRenewal
            );
        }

        return filtered;
    }, [allFlexDiscounts]);

    const handleCountryChange = async (value: string) => {
        setSelectCountry(value);
        setCountrySearchText(value);
    };

    const handleCountrySearchTextChange = (value: string) => {
        setCountrySearchText(value);
        if (!value) {
            setSelectCountry('');
        }
    };

    const handleMarketSegmentChange = async (value: string) => {
        setSelectMarketSegment(value);
    };

    const handleApplicableOnRenewalChange = async (value: string) => {
        setIsApplicableOnRenewal(value === "true");
    };

    const filteredDiscounts = handleFilterChange(selectCountry, selectMarketSegment, isApplicableOnRenewal);

    const reusableDiscounts = useMemo(
        () => allFlexDiscounts.filter((fd) => fd.discountLockEndDate != null),
        [allFlexDiscounts]
    );

    let content: JSX.Element;
    if (isLoading) {
        content = <CircularProgress size={20} className="flex-discounts-table-loading-spinner" />;
    } else if (isError && errorResponse) {
        content = (
            <ErrorDialog
                isError={true}
                status={errorResponse.httpstatus}
                reason={errorResponse.reason}
                message={errorResponse.message}
                handleDismissErrorDialog={handleDismissErrorDialog}
            />
        );
    } else {
        content = (
            <div>
                <TabView selectedIndex={selectedTab} onSelect={setSelectedTab}>
                        <Tab label="View all the available flex discounts" />
                        {isReusabilityEnabled && <Tab label="Edit Reusable Flex Discounts" />}
                        {isOdDiscountsEnabled && <Tab label="Closed Discount Templates" />}
                        {isOdDiscountsEnabled && <Tab label="Closed Discounts (Customer-Specific)" />}
                    </TabView>

                    {selectedTab === 0 && (
                        <div>
                            <div>
                                <Heading variant='subtitle3'>Select a country to view flex discounts</Heading>
                                <Autocomplete
                                    className="flex-discounts-filter-select"
                                    value={countrySearchText}
                                    getCompletions={getCountryCompletions}
                                    onChange={handleCountrySearchTextChange}
                                    onSelect={handleCountryChange}
                                >
                                    <Textfield autocompleteInput placeholder="Search country" aria-label='Country' />
                                </Autocomplete>
                                <br /><br />
                            </div>
                            <div>
                                <Heading variant='subtitle3'>Select a market segment to view flex discounts</Heading>
                                <Select
                                    aria-label='Market Segment'
                                    value={selectMarketSegment}
                                    options={marketSegmentOptions}
                                    onSelect={handleMarketSegmentChange}
                                />
                                <br />
                                <br />
                            </div>
                            {(isApplicableOnRenewalFlagEnabled() || isApplicableOnRenewalFlagDevEnabled()) && (
                                <div>
                                    <Heading variant='subtitle3'>Applicable on Renewal</Heading>
                                    <Select
                                        aria-label='Applicable on Renewal'
                                        value={isApplicableOnRenewal ? "true" : "false"}
                                        options={applicableOnRenewalOptions}
                                        onSelect={handleApplicableOnRenewalChange}
                                    />
                                    <br />
                                    <br />
                                    <br />
                                </div>
                            )}
                            <div>
                                <Accordion
                                    expanded={expanded}
                                    TransitionProps={{ timeout: 400 }}
                                    className='custom-flex-accordion'
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        onClick={() => setExpanded(!expanded)}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header"
                                    >
                                        <Heading variant='subtitle1'>Flex Discounts</Heading>
                                        <Heading variant='subtitle3'>(click to expand)</Heading>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {filteredDiscounts.map((discount, index) => {
                                            const {
                                                applicableCountry,
                                                applicableMarketSegment,
                                                applicableOnRenewal,
                                                applicableCountries,
                                                reusableUntil,
                                                ...rest
                                            } = discount;
                                            return (
                                                <div key={index} className="flex-discounts-item">
                                                    <Heading variant="subtitle3">
                                                        Applicable Market Segment: {applicableMarketSegment}
                                                    </Heading>
                                                    <Heading variant="subtitle3">
                                                        Applicable Country: {applicableCountry}
                                                    </Heading>
                                                    {(isApplicableOnRenewalFlagEnabled() || isApplicableOnRenewalFlagDevEnabled()) && (
                                                        <Heading variant="subtitle3">
                                                            Applicable On Renewal: {applicableOnRenewal ? "Yes" : "No"}
                                                        </Heading>
                                                    )}
                                                    <pre>{JSON.stringify(rest, null, 2)}</pre>
                                                </div>
                                            );
                                        })}
                                    </AccordionDetails>
                                </Accordion>
                            </div>
                        </div>
                    )}

                    {selectedTab === 1 && isReusabilityEnabled && (
                        <div>
                            {reusableDiscounts.length > 0 ? (
                                <EditReusableFlexDiscounts reusableDiscounts={reusableDiscounts} />
                            ) : (
                                <Heading variant='subtitle3'>No reusable flex discounts available.</Heading>
                            )}
                        </div>
                    )}

                    {selectedTab === flexDiscountTemplatesTabIndex && isOdDiscountsEnabled && (
                        <FlexDiscountTemplatesTab
                            partnerId={currentPartnerId}
                        />
                    )}

                    {selectedTab === closedDiscountsTabIndex && isOdDiscountsEnabled && (
                        <ClosedDiscountsTab
                            partnerId={currentPartnerId}
                        />
                    )}
                </div>
        );
    }

    return (
        <div>
            <div className="flex-discounts-heading-container">
                <Heading variant="subtitle1">View and Update Flex Discounts</Heading>
            </div>
            <Heading variant="subtitle3">View all the available flex discounts.</Heading>
            <br />
            {content}
        </div>
    );
}

export default ViewEditAvailableFlexDiscountsPage;